import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Phone, ShieldCheck, UserRound, Cake, Loader2, UtensilsCrossed } from 'lucide-react';
import { useCustomerAuth } from './CustomerAuthContext';
import { useOutlets } from './OutletsContext';
import { useBranches } from './BranchesContext';
type Step = 'phone' | 'otp' | 'profile';
export function CustomerAuth() {
  const navigate = useNavigate();
  const {
    sendOtp,
    verifyOtp,
    completeSignup
  } = useCustomerAuth();
  const {
    outlets
  } = useOutlets();
  const {
    branchId
  } = useBranches();
  // Resolve outlet (for branding + to return the customer to where they were)
  const searchParams = new URLSearchParams(window.location.search);
  const urlOutletId = searchParams.get('outlet');
  const branchOutlets = outlets.filter(o => o.branchId === branchId);
  const activeOutlet = urlOutletId ? branchOutlets.find(o => o.id === urlOutletId) || branchOutlets[0] : branchOutlets[0];
  const accent = activeOutlet?.color || 'orange';
  const menuHref = activeOutlet ? `/menu?outlet=${activeOutlet.id}` : '/menu';
  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [birthday, setBirthday] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn(s => s - 1), 1000);
    return () => clearInterval(t);
  }, [resendIn]);
  const phoneValid = phone.replace(/\D/g, '').length >= 9;
  const otpValue = otp.join('');
  const handleSendOtp = async () => {
    if (!phoneValid || loading) return;
    setError(null);
    setLoading(true);
    try {
      await sendOtp(`+251${phone.replace(/\D/g, '')}`);
      setStep('otp');
      setResendIn(30);
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } finally {
      setLoading(false);
    }
  };
  const handleOtpChange = (i: number, val: string) => {
    const digit = val.replace(/\D/g, '').slice(-1);
    setOtp(prev => {
      const next = [...prev];
      next[i] = digit;
      return next;
    });
    if (digit && i < 5) otpRefs.current[i + 1]?.focus();
  };
  const handleOtpKey = (i: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) otpRefs.current[i - 1]?.focus();
  };
  const handleVerify = async () => {
    if (otpValue.length < 6 || loading) return;
    setError(null);
    setLoading(true);
    try {
      const {
        isNewUser
      } = await verifyOtp(otpValue);
      if (isNewUser) {
        setStep('profile');
      } else {
        navigate(menuHref);
      }
    } catch {
      setError('That code didn’t work. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  const handleComplete = () => {
    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your first and last name.');
      return;
    }
    completeSignup(firstName.trim(), lastName.trim(), birthday);
    navigate(menuHref);
  };
  const goBack = () => {
    setError(null);
    if (step === 'otp') setStep('phone');else if (step === 'profile') setStep('otp');else navigate(menuHref);
  };
  return <div className="min-h-screen w-full bg-[#f7f7f5] font-sans flex flex-col" data-id="element-1374">
      {/* Top bar */}
      <div className="px-4 sm:px-6 py-4" data-id="element-1375">
        <button onClick={goBack} className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors" data-id="element-1376">
          <ArrowLeft size={16} data-id="element-1377" />
          Back
        </button>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 pb-16" data-id="element-1378">
        <div className="w-full max-w-sm" data-id="element-1379">
          {/* Brand */}
          <div className="flex flex-col items-center text-center mb-8" data-id="element-1380">
            {activeOutlet?.branding?.logoUrl ? <img src={activeOutlet.branding.logoUrl} alt="" className="w-16 h-16 rounded-2xl object-cover shadow-sm mb-4" data-id="element-1381" /> : <div className={`w-16 h-16 rounded-2xl flex items-center justify-center bg-${accent}-100 text-${accent}-600 mb-4`} data-id="element-1382">
                <UtensilsCrossed size={28} data-id="element-1383" />
              </div>}
            <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight" data-id="element-1384">
              {step === 'profile' ? 'Almost there' : 'Sign in or sign up'}
            </h1>
            <p className="text-sm text-gray-500 mt-1.5 max-w-xs" data-id="element-1385">
              {step === 'phone' && 'Enter your phone number to order and track your meals.'}
              {step === 'otp' && `We sent a 6-digit code to +251 ${phone}.`}
              {step === 'profile' && 'Tell us a little about you to finish your account.'}
            </p>
          </div>

          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2 mb-8" data-id="element-1386">
            {(['phone', 'otp', 'profile'] as Step[]).map((s, i) => {
            const order = ['phone', 'otp', 'profile'];
            const done = order.indexOf(step) > i;
            const current = step === s;
            return <div key={s} className={`h-1.5 rounded-full transition-all ${current ? `w-8 bg-${accent}-500` : done ? `w-4 bg-${accent}-400` : 'w-4 bg-gray-200'}`} data-id="element-1387" />;
          })}
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5" data-id="element-1388">
            <AnimatePresence mode="wait" data-id="element-1389">
              {step === 'phone' && <motion.div key="phone" initial={{
              opacity: 0,
              x: 12
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -12
            }} transition={{
              duration: 0.2
            }} className="space-y-4" data-id="element-1390">
                  <label className="block" data-id="element-1391">
                    <span className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-1392">
                      Phone number
                    </span>
                    <div className="flex items-center rounded-xl border border-gray-200 focus-within:border-gray-400 overflow-hidden" data-id="element-1393">
                      <span className="flex items-center gap-1.5 px-3 py-3 bg-gray-50 text-gray-600 text-sm font-semibold border-r border-gray-200" data-id="element-1394">
                        <Phone size={14} data-id="element-1395" />
                        +251
                      </span>
                      <input value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 9))} onKeyDown={e => e.key === 'Enter' && handleSendOtp()} inputMode="numeric" placeholder="9XX XXX XXX" className="flex-1 px-3 py-3 text-sm focus:outline-none" data-id="element-1396" />
                    </div>
                  </label>
                  <PrimaryButton accent={accent} onClick={handleSendOtp} disabled={!phoneValid || loading} loading={loading} data-id="element-1397">
                    Send code
                  </PrimaryButton>
                </motion.div>}

              {step === 'otp' && <motion.div key="otp" initial={{
              opacity: 0,
              x: 12
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -12
            }} transition={{
              duration: 0.2
            }} className="space-y-4" data-id="element-1398">
                  <div className="flex justify-center mb-1" data-id="element-1399">
                    <div className={`w-11 h-11 rounded-full flex items-center justify-center bg-${accent}-50 text-${accent}-600`} data-id="element-1400">
                      <ShieldCheck size={22} data-id="element-1401" />
                    </div>
                  </div>
                  <div className="flex justify-between gap-2" data-id="element-1402">
                    {otp.map((d, i) => <input key={i} ref={el => otpRefs.current[i] = el} value={d} onChange={e => handleOtpChange(i, e.target.value)} onKeyDown={e => handleOtpKey(i, e)} inputMode="numeric" maxLength={1} className={`w-11 sm:w-12 h-14 text-center text-xl font-bold rounded-xl border ${d ? `border-${accent}-400 bg-${accent}-50/40` : 'border-gray-200'} focus:outline-none focus:border-${accent}-500 focus:ring-2 focus:ring-${accent}-100 transition`} data-id="element-1403" />)}
                  </div>
                  <PrimaryButton accent={accent} onClick={handleVerify} disabled={otpValue.length < 6 || loading} loading={loading} data-id="element-1404">
                    Verify
                  </PrimaryButton>
                  <div className="text-center" data-id="element-1405">
                    {resendIn > 0 ? <span className="text-xs text-gray-400" data-id="element-1406">
                        Resend code in {resendIn}s
                      </span> : <button onClick={handleSendOtp} className={`text-xs font-semibold text-${accent}-600 hover:underline`} data-id="element-1407">
                        Resend code
                      </button>}
                  </div>
                </motion.div>}

              {step === 'profile' && <motion.div key="profile" initial={{
              opacity: 0,
              x: 12
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -12
            }} transition={{
              duration: 0.2
            }} className="space-y-4" data-id="element-1408">
                  <label className="block" data-id="element-1409">
                    <span className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-1410">
                      Name
                    </span>
                    <div className="flex gap-3" data-id="element-1411">
                      <div className="flex-1 flex items-center rounded-xl border border-gray-200 focus-within:border-gray-400 overflow-hidden" data-id="element-1412">
                        <span className="px-3 py-3 text-gray-400 border-r border-gray-200" data-id="element-1413">
                          <UserRound size={16} data-id="element-1414" />
                        </span>
                        <input value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="First name" className="flex-1 px-3 py-3 text-sm focus:outline-none w-full min-w-0" data-id="element-1415" />
                      </div>
                      <div className="flex-1 flex items-center rounded-xl border border-gray-200 focus-within:border-gray-400 overflow-hidden" data-id="element-1416">
                        <input value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Last name" className="flex-1 px-3 py-3 text-sm focus:outline-none w-full min-w-0" data-id="element-1417" />
                      </div>
                    </div>
                  </label>
                  <label className="block" data-id="element-1418">
                    <span className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-1419">
                      Birthday{' '}
                      <span className="text-gray-400 normal-case font-normal" data-id="element-1420">
                        (optional)
                      </span>
                    </span>
                    <div className="flex items-center rounded-xl border border-gray-200 focus-within:border-gray-400 overflow-hidden" data-id="element-1421">
                      <span className="px-3 py-3 text-gray-400 border-r border-gray-200" data-id="element-1422">
                        <Cake size={16} data-id="element-1423" />
                      </span>
                      <input type="date" value={birthday} onChange={e => setBirthday(e.target.value)} className="flex-1 px-3 py-3 text-sm focus:outline-none text-gray-700" data-id="element-1424" />
                    </div>
                    <span className="text-[11px] text-gray-400 mt-1.5 block" data-id="element-1425">
                      We’ll send you a treat on your special day.
                    </span>
                  </label>
                  <PrimaryButton accent={accent} onClick={handleComplete} data-id="element-1426">
                    Create account
                  </PrimaryButton>
                </motion.div>}
            </AnimatePresence>

            {error && <p className="text-xs text-rose-600 font-medium mt-3 text-center" data-id="element-1427">
                {error}
              </p>}
          </div>

          <p className="text-[11px] text-gray-400 text-center mt-5 leading-relaxed" data-id="element-1428">
            By continuing you agree to our Terms of Service and Privacy Policy.
          </p>
        </div>
      </div>
    </div>;
}
function PrimaryButton({
  accent,
  children,
  onClick,
  disabled,
  loading
}: {
  accent: string;
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
}) {
  return <button onClick={onClick} disabled={disabled} className={`w-full py-3.5 rounded-xl text-sm font-bold text-white transition-colors flex items-center justify-center gap-2 ${disabled ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : `bg-${accent}-500 hover:bg-${accent}-600`}`} data-id="element-1429">
      {loading && <Loader2 size={16} className="animate-spin" data-id="element-1430" />}
      {children}
    </button>;
}